import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MicroServiceHttpService } from './microServiceHttp.service';
import type {
  GetAiProvidersResponse,
  GetAuthTokenStatusResponse,
  RotateAuthTokenResponseBody,
  UpdateAiProviderApiKeyBody,
  UpdateAiProviderApiKeyResponse,
  UpdateAuthTokenEnabledBody,
  UpdateAuthTokenEnabledResponse,
  GetChatbotSecurityUsersResponse,
  GetAllUsersResponse,
  UpdateChatbotSecurityUsersBody,
} from '../models/chatbot-security.model';

function unwrapApiPayload<T>(raw: unknown): T | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('data' in o && o['data'] != null && typeof o['data'] === 'object') {
    return o['data'] as T;
  }
  return raw as T;
}

function parseAiProviders(raw: unknown): GetAiProvidersResponse {
  const body = unwrapApiPayload<unknown>(raw);
  if (body == null || typeof body !== 'object') {
    return { providers: [] };
  }
  const p = (body as Record<string, unknown>)['providers'];
  if (!Array.isArray(p)) {
    return { providers: [] };
  }
  return { providers: p as GetAiProvidersResponse['providers'] };
}

function parseAuthTokenStatus(raw: unknown): GetAuthTokenStatusResponse {
  const body = unwrapApiPayload<unknown>(raw);
  if (body == null || typeof body !== 'object') {
    return { enabled: false, token: null, expiryDate: null };
  }
  const o = body as Record<string, unknown>;
  return {
    enabled: Boolean(o['enabled']),
    token: typeof o['token'] === 'string' ? o['token'] : null,
    expiryDate: typeof o['expiryDate'] === 'string' ? o['expiryDate'] : null,
  };
}

function parseBotSecurityUserList(raw: unknown): GetChatbotSecurityUsersResponse {
  const body = unwrapApiPayload<unknown>(raw);
  if (body == null || typeof body !== 'object') {
    return { userList: [] };
  }
  const u = (body as Record<string, unknown>)['userList'];
  return { userList: Array.isArray(u) ? u : [] };
}

function parseAllUsers(raw: unknown): GetAllUsersResponse {
  const body = unwrapApiPayload<unknown>(raw);
  if (body == null || typeof body !== 'object') {
    return { users: [] };
  }
  const u = (body as Record<string, unknown>)['users'];
  return { users: Array.isArray(u) ? (u as GetAllUsersResponse['users']) : [] };
}

@Injectable({ providedIn: 'root' })
export class ChatbotSecurityService {
  private readonly microHttp = inject(MicroServiceHttpService);

  getAiProviders(chatbotId: number): Observable<GetAiProvidersResponse> {
    return this.microHttp
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/ai-providers`, {
        showToast: false,
        showLoader: false,
      })
      .pipe(map(parseAiProviders));
  }

  updateAiProviderApiKey(
    chatbotId: number,
    body: UpdateAiProviderApiKeyBody
  ): Observable<UpdateAiProviderApiKeyResponse> {
    return this.microHttp
      .put<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/ai-provider-api-key`, body, {
        showToast: false,
        showLoader: true,
      })
      .pipe(
        map((raw) => {
          const u = unwrapApiPayload<unknown>(raw) ?? raw;
          if (u != null && typeof u === 'object' && (u as Record<string, unknown>)['success'] === true) {
            return { success: true as const };
          }
          return { success: true as const };
        })
      );
  }

  getAuthTokenStatus(chatbotId: number): Observable<GetAuthTokenStatusResponse> {
    return this.microHttp
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/auth-token`, {
        showToast: false,
        showLoader: false,
      })
      .pipe(map(parseAuthTokenStatus));
  }

  updateAuthTokenEnabled(
    chatbotId: number,
    body: UpdateAuthTokenEnabledBody
  ): Observable<UpdateAuthTokenEnabledResponse> {
    return this.microHttp
      .put<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/auth-token`, body, {
        showToast: false,
        showLoader: true,
      })
      .pipe(
        map((raw) => {
          const u = unwrapApiPayload<unknown>(raw) ?? raw;
          if (u == null || typeof u !== 'object') {
            return { success: true as const };
          }
          const o = u as Record<string, unknown>;
          if (o['success'] !== true) {
            return { success: true as const };
          }
          if ('token' in o || 'expiryDate' in o) {
            return {
              success: true as const,
              token: typeof o['token'] === 'string' ? o['token'] : o['token'] === null ? null : null,
              expiryDate:
                typeof o['expiryDate'] === 'string'
                  ? o['expiryDate']
                  : o['expiryDate'] === null
                    ? null
                    : null,
            };
          }
          return { success: true as const };
        })
      );
  }

  /**
   * POST `chatbot/bot/:id/security/auth-token/rotate` — empty body; new token in `data`.
   */
  rotateAuthToken(chatbotId: number): Observable<RotateAuthTokenResponseBody> {
    return this.microHttp
      .post<unknown>(
        'whitelabel',
        `chatbot/bot/${chatbotId}/security/auth-token/rotate`,
        null,
        {
          showToast: false,
          showLoader: true,
        }
      )
      .pipe(
        map((raw) => {
          const body = unwrapApiPayload<unknown>(raw);
          if (body == null || typeof body !== 'object') {
            throw new Error('Invalid rotate token response');
          }
          const o = body as Record<string, unknown>;
          const token = typeof o['token'] === 'string' ? o['token'] : '';
          const expiryDate =
            typeof o['expiryDate'] === 'string' ? o['expiryDate'] : o['expiryDate'] === null ? null : null;
          return { token, expiryDate } satisfies RotateAuthTokenResponseBody;
        })
      );
  }

  getBotSecurityUserList(chatbotId: number): Observable<GetChatbotSecurityUsersResponse> {
    return this.microHttp
      .get<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/user-list`, {
        showToast: false,
        showLoader: false,
      })
      .pipe(map(parseBotSecurityUserList));
  }

  getAllUsers(): Observable<GetAllUsersResponse> {
    return this.microHttp
      .get<unknown>('whitelabel', 'users', {
        showToast: false,
        showLoader: false,
      })
      .pipe(map(parseAllUsers));
  }

  updateBotSecurityUserList(
    chatbotId: number,
    body: UpdateChatbotSecurityUsersBody
  ): Observable<{ success: boolean }> {
    return this.microHttp
      .post<unknown>('whitelabel', `chatbot/bot/${chatbotId}/security/user-list`, body, {
        showToast: false,
        showLoader: false,
      })
      .pipe(
        map((raw) => {
          const u = unwrapApiPayload<unknown>(raw) ?? raw;
          if (u != null && typeof u === 'object' && (u as Record<string, unknown>)['success'] === true) {
            return { success: true };
          }
          return { success: true };
        })
      );
  }
}
